import { z } from 'zod';

// ── Адрес электронной почты ──
// Одно правило на портал: адрес учётной записи, адрес водителя в справочнике и адрес, который
// приходит кадровым импортом, проверяются одинаково. Разойдись они — импорт заводил бы записи,
// которые форма потом отказывается сохранять, а рассылка спотыкалась бы на них уже в SMTP.
//
// Регистр не приводится: в БД адрес лежит в `citext`, и сравнение уже нечувствительно к регистру,
// а показывать человеку его же адрес переписанным в нижний регистр незачем — так он его не писал.

/** Верхняя граница длины: та же, что у адреса учётки в `users`. */
const EMAIL_MAX = 255;

export const EMAIL_FORMAT_MESSAGE = 'Некорректный email';

/**
 * Приведение введённого адреса к тому, что человек имел в виду: снимаются **все** пробельные
 * символы, а не только крайние.
 *
 * Внутренние — не педантизм: адрес попадает в поле копипастой из письма, из таблицы и из
 * мессенджера, и переносом строки в них разрывается что угодно — `davidovsergej777@gmail. com`
 * человек читает как свой адрес, а отказ по пробелу перед `com` читает как «портал не принимает
 * gmail». Пробела в адресе не бывает (кавычечную форму `"ivan ivanov"@…` портал не принимает и
 * так), поэтому убрать его — единственное толкование, которое не отвергает верный адрес.
 *
 * Вместе с пробелами снимаются символы нулевой ширины: `\s` покрывает неразрывный пробел и BOM,
 * `\u200b–\u200d` — остальные. Их не видно ни в поле, ни в сообщении об ошибке, и адрес с ними
 * выглядит правильным до последнего.
 *
 * Регистр по-прежнему не трогаем — см. заголовок файла.
 */
export function normalizeEmail(value: string): string {
  return value.replace(/[\s\u200b-\u200d]/g, '');
}

/** Правило формата — общее для обязательного и необязательного адреса. */
const emailFormat = z.string().max(EMAIL_MAX).pipe(z.email(EMAIL_FORMAT_MESSAGE));

/** Адрес там, где он обязателен: вход, регистрация, заведение учётки администратором. */
export const emailSchema = z.string().transform(normalizeEmail).pipe(emailFormat);

/**
 * Тот же адрес там, где его вправе не оставить: пустая строка — «не указан» (email водителя).
 * Правило то же самое — необязательность не делает годным «-» или «нет»: такое поле выглядит
 * заполненным, а письмо по нему не уйдёт.
 */
export const optionalEmailSchema = z
  .string()
  .transform(normalizeEmail)
  .pipe(
    z
      .string()
      .max(EMAIL_MAX)
      .refine((v) => v === '' || z.email().safeParse(v).success, EMAIL_FORMAT_MESSAGE),
  );

// ── Свой домен и чужой (ADR 0090) ──

/**
 * Домены компании. Список короткий и меняется раз в годы, поэтому он константа, а не настройка
 * окружения: настройкой его пришлось бы держать одинаковым и у сервера, и в сборке портала, а
 * разойдись они — портал предупреждал бы о «внешнем» адресе, который для сервера свой.
 */
export const INTERNAL_EMAIL_DOMAINS = ['su10.ru', 'zakupka.pro', 'mstroy.pro'] as const;

/**
 * Адрес в домене компании — сам домен или его поддомен (`auto.su10.ru`). Ничего не решает и никого
 * не пускает: признак нужен предупреждению на форме регистрации и пометке в списке заявок.
 *
 * Сравнивается хвост после **последней** `@` и только целыми метками домена: `su10.ru.example.com`
 * и `nesu10.ru` — чужие, как бы похоже они ни выглядели.
 */
export function isInternalEmail(email: string): boolean {
  const at = email.lastIndexOf('@');
  if (at < 0) return false;
  const domain = email
    .slice(at + 1)
    .trim()
    .toLowerCase();
  return INTERNAL_EMAIL_DOMAINS.some((own) => domain === own || domain.endsWith(`.${own}`));
}
